//helpdesk
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Layout from "../../Component/Layout";
import axios from "axios";
import Select, { components } from "react-select";
import { MASTER_API } from "../../apiConfig";

/* ==========================================================
   NOTE ON CONFIGURATION ENDPOINTS
   Module / Process / Sub Process are NOT independent lists —
   they come from the single "module-process-config" table
   (module_name, process_name, sub_process_name triplets) so
   Process depends on the selected Module, and Sub Process
   depends on the selected Module + Process. Developer, Priority
   and Ticket Status are plain lookup lists, same pattern as
   Product.js (`${MASTER_API}/configuration/...`).
   ========================================================== */
const CONFIG_ENDPOINTS = {
  moduleProcessConfig: `${MASTER_API}/configuration/dynamic/module-process-config`,
  developer: `${MASTER_API}/configuration/dynamic/developer`,
  priority: `${MASTER_API}/configuration/dynamic/priority`,
  ticketStatus: `${MASTER_API}/configuration/dynamic/ticket-status`
};

/* ==========================================================
   DATE FIELD
   Native <input type="date"> renders its text in whatever
   format the OS/browser locale dictates (mm/dd/yyyy,
   dd-mm-yyyy, etc). To keep the display consistent everywhere
   (dd/mm/yyyy) regardless of the machine viewing it, the native
   text is made transparent and a formatted label is drawn on
   top; the native input (and its calendar icon) still handles
   picking/storing the date as a normal yyyy-mm-dd value.
   ========================================================== */
const formatDMY = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

function DateField({ value, onChange, readOnly }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        type="date"
        className="form-control hd-date-input"
        style={{
          ...styles.compactInput,
          color: "transparent",
          background: readOnly ? "#f8fafc" : "#fff"
        }}
        value={value || ""}
        onChange={onChange}
        readOnly={readOnly}
      />
      <span
        style={{
          position: "absolute",
          left: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: "13px",
          color: value ? "#1e293b" : "#94a3b8",
          pointerEvents: "none"
        }}
      >
        {value ? formatDMY(value) : "dd/mm/yyyy"}
      </span>
    </div>
  );
}

/* ==========================================================
   EXPORT (CSV)
   Exports every ticket matching the active filters across ALL
   status tabs (active, deployed, customer action, closed, hold). The
   selected tab is ignored; with no filters, everything is
   exported. Opens cleanly in Excel: a
   UTF-8 BOM is added and every cell is quoted/escaped.
   ========================================================== */
const EXPORT_COLUMNS = [
  { header: "Ticket No", get: (t) => t.ticket_no },
  { header: "Module", get: (t) => t.module_name },
  { header: "Process", get: (t) => t.process_name },
  { header: "Sub Process", get: (t) => t.sub_process_name },
  { header: "Description", get: (t) => t.ticket_description },
  { header: "Created By", get: (t) => t.registered_by },
  {
    header: "Created Date",
    get: (t) =>
      t.creation_date ? formatDMY(String(t.creation_date).split("T")[0]) : ""
  },
  { header: "Ageing (Days)", get: (t) => getTicketAgeing(t) },
  { header: "Priority", get: (t) => t.priority },
  { header: "Developer", get: (t) => t.developer },
  { header: "Resolution Provided", get: (t) => t.resolution_provided },
  { header: "Status", get: (t) => t.developer_status },
  { header: "Commit Name", get: (t) => t.commit_name }
];

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const exportTicketsToCsv = (rows, fileName) => {
  const lines = [
    EXPORT_COLUMNS.map((c) => csvCell(c.header)).join(","),
    ...rows.map((t) => EXPORT_COLUMNS.map((c) => csvCell(c.get(t))).join(","))
  ];
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const styles = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    padding: "20px",
  },

  pageTitleBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px"
  },

  pageTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 700,
    color: "#1e293b",
  },

  pageSubtitle: {
    margin: "2px 0 0 0",
    fontSize: "12px",
    color: "#64748b",
  },

  createBtn: {
    background: "#021843",
    color: "#fff",
    border: "1px solid #021843",
    padding: "8px 18px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer"
  },

  filterCard: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 15,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: 15
  },

  compactLabel: {
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "2px",
    color: "#334155",
    display: "block",
  },

  compactInput: {
    fontSize: "13px",
    height: "32px",
    padding: "4px 8px",
  },

  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
  },

  erpTable: { width: "100%", borderCollapse: "collapse" },

  erpTh: {
    background: "#f8fafc",
    fontSize: 12,
    fontWeight: 600,
    padding: "10px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
    whiteSpace: "nowrap"
  },

  erpTd: {
    fontSize: 13,
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155"
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999
  },

  modal: {
    width: "95vw",
    maxWidth: "1200px",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "10px"
  },

  alertBox: (type) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 500,
    marginBottom: "14px",
    background: type === "success" ? "#dcfce7" : "#fdecea",
    color: type === "success" ? "#166534" : "#b91c1c",
    border: type === "success" ? "1px solid #86efac" : "1px solid #f5c2c7"
  })
};

const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" }
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center"
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
      ? "#f1f5f9"
      : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px"
  })
};

// Same look as customSelectStyles, but the control/value-container grow and
// wrap instead of clipping at a fixed 32px — needed for isMulti selects
// like the Status filter where more than one tag needs to be visible.
const multiSelectStyles = {
  ...customSelectStyles,
  control: (base) => ({
    ...customSelectStyles.control(base),
    height: "auto",
    minHeight: 32
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    alignItems: "center"
  }),
  multiValue: (base) => ({
    ...base,
    fontSize: "12px"
  })
};

/* ==========================================================
   CHECKBOX MULTI-SELECT (used by the "Created By" filter)
   - every option has a checkbox, menu stays open while ticking
   - first option "Select All" ticks / unticks everything
   - the box shows a short summary instead of a pile of tags:
     the name (1 selected), "N selected", or "All"
   ========================================================== */
const SELECT_ALL_VALUE = "__all__";

const checkboxSelectStyles = {
  ...customSelectStyles,
  valueContainer: (base) => ({
    ...customSelectStyles.valueContainer(base),
    flexWrap: "nowrap",
    overflow: "hidden"
  }),
  // selected rows keep a white background - the checkbox shows the state
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#f1f5f9" : "white",
    color: "#334155",
    cursor: "pointer",
    fontSize: "13px"
  })
};

const CheckboxOption = (props) => {
  const isAll = props.data.value === SELECT_ALL_VALUE;
  const checked = isAll ? !!props.selectProps.allSelected : props.isSelected;
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={checked}
          readOnly
          style={{ pointerEvents: "none", margin: 0 }}
        />
        <span style={{ fontWeight: isAll ? 700 : 400 }}>{props.label}</span>
      </div>
    </components.Option>
  );
};

const CheckboxValueContainer = ({ children, ...props }) => {
  const selected = props.getValue();
  const { allSelected, inputValue } = props.selectProps;
  let summary = null;
  if (selected.length > 0 && !inputValue) {
    summary = allSelected
      ? "All"
      : selected.length === 1
      ? selected[0].label
      : `${selected.length} selected`;
  }
  return (
    <components.ValueContainer {...props}>
      {summary && (
        <span
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%"
          }}
        >
          {summary}
        </span>
      )}
      {children}
    </components.ValueContainer>
  );
};

// Chips are replaced by the summary text above
const NoMultiValue = () => null;

const toOptions = (arr) =>
  (arr || []).map((v) => {
    if (v && typeof v === "object") {
      const val = v.name || v.value || v.label || "";
      return { value: String(val).trim(), label: String(val).trim() };
    }
    return { value: String(v).trim(), label: String(v).trim() };
  });

/* ===================== STATUS TAB BUCKETS ===================== */
const ACTIVE_STATUSES = ["unassigned", "in development", "in unit testing", "developed"];
const DEPLOYED_STATUSES = ["deployed", "in testing"];
const CUSTOMER_ACTION_STATUSES = ["in customer action"];
const CLOSED_STATUSES = ["closed"];
const HOLD_STATUSES = ["hold", "on hold"];

const getStatusTab = (status) => {
  const s = (status || "").trim().toLowerCase();
  if (CLOSED_STATUSES.includes(s)) return "closed";
  if (HOLD_STATUSES.includes(s)) return "hold";
  if (CUSTOMER_ACTION_STATUSES.includes(s)) return "customerAction";
  if (DEPLOYED_STATUSES.includes(s)) return "deployed";
  if (ACTIVE_STATUSES.includes(s)) return "active";
  return "active"; // fallback bucket for any unmapped/blank status
};

// Turn a list of strings (possibly with blanks/dupes) into unique {value,label} options
const uniqueOptions = (values) => {
  const seen = new Set();
  const out = [];
  (values || []).forEach((v) => {
    const val = String(v || "").trim();
    if (!val || seen.has(val)) return;
    seen.add(val);
    out.push({ value: val, label: val });
  });
  return out;
};

// Local calendar date as yyyy-mm-dd (not UTC, so it never lands on the wrong day)
const todayISO = () => {
  const n = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
};

// Whole calendar days between two dates (yyyy-mm-dd or ISO timestamps).
// Compared as plain calendar dates so time-of-day / timezone never shifts
// the result by a day.
const daysBetween = (fromDate, toDate) => {
  if (!fromDate || !toDate) return "-";
  const [y1, m1, d1] = String(fromDate).split("T")[0].split("-").map(Number);
  const [y2, m2, d2] = String(toDate).split("T")[0].split("-").map(Number);
  if (!y1 || !m1 || !d1 || !y2 || !m2 || !d2) return "-";
  return Math.max(0, Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000));
};

// Ageing (Days):
//  - open ticket   -> creation date to today (keeps running)
//  - closed ticket -> creation date to closed date (stops at closure)
const getTicketAgeing = (t) => {
  if (!t || !t.creation_date) return "-";
  if ((t.developer_status || "").trim().toLowerCase() === "closed") {
    return t.closed_date ? daysBetween(t.creation_date, t.closed_date) : "-";
  }
  return daysBetween(t.creation_date, todayISO());
};

/* ==========================================================
   ACTION-BASED DATES
   Development Start / Development End / Deployment / Closed dates
   are stamped by the BACKEND when the ticket's status moves into
   In Development / Developed / Deployed / Closed. They are NOT shown
   in the UI - they only exist so Ageing can be calculated (Closed
   Date stops the ageing clock). Tickets are re-fetched after every
   status-changing action so Ageing stays in sync with the server.
   ========================================================== */

/* ==========================================================
   COMMIT NAMES (multiple)
   A ticket can have more than one commit. In the UI each one is its
   own input row (starts with one; "+ Add Commit" adds more). They are
   stored in the single commit_name field separated by new lines, so
   the backend/database format is unchanged.
   ========================================================== */
const getCommitList = (value) => {
  const list = String(value || "").split("\n");
  return list.length ? list : [""];
};

// Trim every commit, drop blank rows, and join back for saving
const cleanCommitNames = (value) =>
  getCommitList(value)
    .map((c) => c.trim())
    .filter(Boolean)
    .join("\n");

const getCurrentUser = () => {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      const fullName = `${parsedUser.firstName || ""} ${parsedUser.lastName || ""}`.trim();
      if (fullName) return fullName;
      if (parsedUser.email) return parsedUser.email;
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
  }
  const email = localStorage.getItem("email");
  if (email && email !== "undefined" && email !== "null") return email;
  const storedName = localStorage.getItem("userName");
  if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
  return "User";
};

const getCurrentUserId = () => {
  const clean = (v) =>
    v !== undefined && v !== null && String(v).trim() !== "" &&
    String(v) !== "undefined" && String(v) !== "null"
      ? String(v).trim()
      : "";

  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const u = JSON.parse(userObj);
      const id =
        clean(u.userId) ||
        clean(u.user_id) ||
        clean(u.username) ||
        clean(u.userName) ||
        clean(u.employeeId) ||
        clean(u.id);
      if (id) return id;
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
  }

  const stored =
    clean(localStorage.getItem("userId")) ||
    clean(localStorage.getItem("user_id")) ||
    clean(localStorage.getItem("username"));
  if (stored) return stored;

  // last-resort fallback so the field is never blank
  return getCurrentUser();
};

const createEmptyTicket = (ticketNo) => ({
  ticketNo,
  moduleName: "",
  process: "",
  subProcess: "",
  ticketDescription: "",
  resolutionProvided: "",
  commitName: "",
  creationDate: new Date().toISOString().split("T")[0],
  estimatedEfforts: "",
  actualEfforts: "",
  developmentStartDate: "",
  developmentEndDate: "",
  deploymentDate: "",
  closedDate: "",
  estimatedStartDate: "",
  estimatedStartTime: "",
  estimatedClosureDate: "",
  estimatedClosureTime: "",
  actualStartDate: "",
  actualStartTime: "",
  actualClosureDate: "",
  actualClosureTime: "",
  priority: "",
  registeredBy: getCurrentUserId(),
  developer: "",
  developerStatus: "Unassigned",
  pushedStatus: "no"
});

function HelpDesk() {
  const [showModal, setShowModal] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketRows, setTicketRows] = useState([createEmptyTicket(null)]);
  const [editId, setEditId] = useState(null);

  // Ticket pending delete confirmation (null when the confirm modal is closed)
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Resolution prompt shown when a ticket is moved out of "In Development"
  const [resolutionPrompt, setResolutionPrompt] = useState(null);
  // Lightweight toast (floats above modals, auto-dismisses)
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Status tab toggle — buckets tickets by developer_status into
  // Active / Deployed / Customer Action / Closed / Hold
  const [statusTab, setStatusTab] = useState("active");

  // { type: "success" | "error", message: string } | null
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!alert) return;
    const timer = setTimeout(() => setAlert(null), 5000);
    return () => clearTimeout(timer);
  }, [alert]);

  // Pulls a readable message out of an axios error / thrown error
  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

  const [configOptions, setConfigOptions] = useState({
    moduleProcessConfig: [], // raw rows: [{module_name, process_name, sub_process_name}, ...]
    developer: [],
    priority: [],
    ticketStatus: []
  });

  // Created By defaults to the logged-in user, so a user only sees their
  // own tickets until they explicitly clear/change this filter. It's seeded
  // once here using the same id getCurrentUserId() stamps onto
  // registered_by when a ticket is created, so the values match.
  const currentUserId = getCurrentUserId();

  const [filters, setFilters] = useState({
    module: null,
    process: null,
    subProcess: null,
    developer: null,
    priority: null,
    status: [],
    ticketNo: null,
    createdBy: currentUserId ? [{ value: currentUserId, label: currentUserId }] : []
  });

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${MASTER_API}/helpdesk/tickets`);
      setTickets(res.data.data || []);
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", message: getErrorMessage(err, "Failed to load tickets.") });
    }
  }, []);

  const fetchConfigOptions = useCallback(async () => {
    try {
      const [mpcRes, developerRes, priorityRes, ticketStatusRes] =
        await Promise.all([
          axios.get(CONFIG_ENDPOINTS.moduleProcessConfig).catch(() => ({ data: [] })),
          axios.get(CONFIG_ENDPOINTS.developer).catch(() => ({ data: [] })),
          axios.get(CONFIG_ENDPOINTS.priority).catch(() => ({ data: [] })),
          axios.get(CONFIG_ENDPOINTS.ticketStatus).catch(() => ({ data: [] }))
        ]);

      setConfigOptions({
        moduleProcessConfig: mpcRes.data || [],
        developer: developerRes.data || [],
        priority: priorityRes.data || [],
        ticketStatus: ticketStatusRes.data || []
      });
    } catch (err) {
      console.error("Failed to load configuration options", err);
      setAlert({
        type: "error",
        message: getErrorMessage(err, "Failed to load configuration options.")
      });
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchConfigOptions();
  }, [fetchTickets, fetchConfigOptions]);

  const handleEdit = (ticket) => {
    setEditId(ticket.id);

    setTicketRows([
      {
        ticketNo: ticket.ticket_no,
        moduleName: ticket.module_name || "",
        process: ticket.process_name || "",
        subProcess: ticket.sub_process_name || "",
        ticketDescription: ticket.ticket_description || "",
        resolutionProvided: ticket.resolution_provided || "",
        commitName: ticket.commit_name || "",
        creationDate: ticket.creation_date
          ? ticket.creation_date.split("T")[0]
          : "",
        estimatedEfforts: ticket.estimated_efforts || "",
        actualEfforts: ticket.actual_efforts || "",
        developmentStartDate: ticket.development_start_date ? String(ticket.development_start_date).split("T")[0] : "",
        developmentEndDate: ticket.development_end_date ? String(ticket.development_end_date).split("T")[0] : "",
        deploymentDate: ticket.deployment_date ? String(ticket.deployment_date).split("T")[0] : "",
        closedDate: ticket.closed_date ? String(ticket.closed_date).split("T")[0] : "",
        estimatedStartDate: ticket.estimated_start_date
          ? ticket.estimated_start_date.split("T")[0]
          : "",
        estimatedStartTime: ticket.estimated_start_time
          ? ticket.estimated_start_time.slice(0, 5)
          : "",
        estimatedClosureDate: ticket.estimated_closure_date
          ? ticket.estimated_closure_date.split("T")[0]
          : "",
        estimatedClosureTime: ticket.estimated_closure_time
          ? ticket.estimated_closure_time.slice(0, 5)
          : "",
        actualStartDate: ticket.actual_start_date
          ? ticket.actual_start_date.split("T")[0]
          : "",
        actualStartTime: ticket.actual_start_time
          ? ticket.actual_start_time.slice(0, 5)
          : "",
        actualClosureDate: ticket.actual_closure_date
          ? ticket.actual_closure_date.split("T")[0]
          : "",
        actualClosureTime: ticket.actual_closure_time
          ? ticket.actual_closure_time.slice(0, 5)
          : "",
        priority: ticket.priority || "",
        registeredBy: ticket.registered_by || getCurrentUserId(),
        developer: ticket.developer || "",
        developerStatus: ticket.developer_status || "",
        pushedStatus: ticket.pushed_status || "no"
      }
    ]);

    setAlert(null);
    setShowModal(true);
  };

  const handleChange = (index, field, value) => {
    const updated = [...ticketRows];

    if (field === "moduleName") {
      // Module changed: clear process/subProcess since they no longer apply
      updated[index] = { ...updated[index], moduleName: value, process: "", subProcess: "" };
    } else if (field === "process") {
      // Process changed: clear subProcess since it no longer applies
      updated[index] = { ...updated[index], process: value, subProcess: "" };
    } else if (field === "subProcess") {
      // Sub Process picked directly: auto-fill Module + Process from the
      // matching module-process-config row.
      const match = configOptions.moduleProcessConfig.find(
        (r) => (r.sub_process_name || "").trim() === (value || "").trim()
      );
      updated[index] = {
        ...updated[index],
        subProcess: value,
        moduleName: match ? (match.module_name || "").trim() : updated[index].moduleName,
        process: match ? (match.process_name || "").trim() : updated[index].process
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    setTicketRows(updated);
  };

  const removeRow = (index) => {
    const updated = ticketRows.filter((_, i) => i !== index);
    setTicketRows(updated);
  };

  const handleSave = async () => {
    // Module is mandatory — a ticket cannot be raised without it
    const missingModuleRow = ticketRows.findIndex((row) => !row.moduleName);
    if (missingModuleRow !== -1) {
      setAlert({
        type: "error",
        message:
          ticketRows.length > 1
            ? `Module is required for row #${missingModuleRow + 1}. Please select a module before saving.`
            : "Module is required. Please select a module before saving the ticket."
      });
      return;
    }

    // Moving a ticket out of "In Development" requires a resolution
    if (editId) {
      const original = tickets.find((t) => t.id === editId);
      const row = ticketRows[0];
      const wasInDev = (original?.developer_status || "").trim().toLowerCase() === "in development";
      const stillInDev = (row.developerStatus || "").trim().toLowerCase() === "in development";
      if (wasInDev && !stillInDev && !(row.resolutionProvided || "").trim()) {
        setToast({
          type: "error",
          message:
            "Resolution Provided is required when changing the status from In Development."
        });
        return;
      }
    }

    // Blank commit rows are dropped before saving
    const cleanedRows = ticketRows.map((row) => ({
      ...row,
      commitName: cleanCommitNames(row.commitName)
    }));

    try {
      let createdTicketNos = [];

      if (editId) {
        await axios.put(`${MASTER_API}/helpdesk/update/${editId}`, cleanedRows[0]);
      } else {
        for (let row of cleanedRows) {
          // ticket_no is assigned by the server as part of creation, not
          // sent up from here, so pull it back out of the response.
          const res = await axios.post(`${MASTER_API}/helpdesk/create`, row);
          const ticketNo = res?.data?.data?.ticket_no;
          if (ticketNo) createdTicketNos.push(ticketNo);
        }
      }

      await fetchTickets();
      setShowModal(false);
      setEditId(null);
      setTicketRows([createEmptyTicket(null)]);
      setAlert({
        type: "success",
        message: editId
          ? "Ticket updated successfully."
          : createdTicketNos.length
          ? `Ticket(s) created successfully. Ticket #${createdTicketNos.join(", #")} assigned.`
          : "Ticket(s) created successfully."
      });
    } catch (err) {
      console.error(err);
      const saveError = getErrorMessage(err, "Failed to save the ticket. Please try again.");
      if (err?.response?.status === 400) {
        setToast({ type: "error", message: saveError });
      } else {
        setAlert({ type: "error", message: saveError });
      }
    }
  };

  // True when this change would move the ticket out of "In Development"
  const leavesInDevelopment = (ticket, newStatus) =>
    (ticket?.developer_status || "").trim().toLowerCase() === "in development" &&
    (newStatus || "").trim().toLowerCase() !== "in development";

  const handlePushedChange = (id, pushedStatus) => {
    const ticket = tickets.find((t) => t.id === id);
    // Pushed = "yes" bumps the status to Deployed, i.e. leaves In Development
    if (pushedStatus === "yes" && leavesInDevelopment(ticket, "Deployed")) {
      setResolutionPrompt({ id, kind: "pushed", value: pushedStatus, text: ticket.resolution_provided || "" });
      return;
    }
    return submitPushedChange(id, pushedStatus);
  };

  const submitPushedChange = async (id, pushedStatus, resolutionProvided) => {
    // Optimistic update so the dropdown reflects the change immediately.
    // Marking a ticket as Pushed also means it has gone live, so we bump
    // its status to "Deployed" at the same time.
    setTickets((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              pushed_status: pushedStatus,
              developer_status: pushedStatus === "yes" ? "Deployed" : t.developer_status
            }
          : t
      )
    );

    try {
      await axios.put(`${MASTER_API}/helpdesk/update-pushed/${id}`, {
        pushedStatus,
        ...(resolutionProvided !== undefined && { resolutionProvided })
      });
      await fetchTickets(); // pick up the server-stamped Deployment Date
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message: getErrorMessage(err, "Failed to update pushed status. Please try again.")
      });
      await fetchTickets();
    }
  };

  const handleDeveloperChange = async (id, developer) => {
    // Optimistic update so the dropdown reflects the change immediately.
    // Assigning/changing the developer also means work is starting, so we
    // bump the status to "In Development" at the same time (unless the
    // developer is being cleared/unassigned).
    setTickets((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              developer,
              developer_status: developer ? "In Development" : t.developer_status
            }
          : t
      )
    );

    try {
      await axios.put(`${MASTER_API}/helpdesk/update-developer/${id}`, { developer });
      await fetchTickets(); // pick up the server-stamped Development Start Date
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message: getErrorMessage(err, "Failed to update developer. Please try again.")
      });
      await fetchTickets();
    }
  };

  const handleStatusChange = (id, developerStatus) => {
    const ticket = tickets.find((t) => t.id === id);
    if (leavesInDevelopment(ticket, developerStatus)) {
      setResolutionPrompt({ id, kind: "status", value: developerStatus, text: ticket.resolution_provided || "" });
      return;
    }
    return submitStatusChange(id, developerStatus);
  };

  const submitStatusChange = async (id, developerStatus, resolutionProvided) => {
    // Optimistic update so the table reflects the change immediately
    const prevTickets = tickets;
    setTickets((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              developer_status: developerStatus,
              ...(resolutionProvided !== undefined && { resolution_provided: resolutionProvided })
            }
          : t
      )
    );

    try {
      await axios.put(`${MASTER_API}/helpdesk/update-status/${id}`, {
        developerStatus,
        ...(resolutionProvided !== undefined && { resolutionProvided })
      });
      await fetchTickets(); // pick up the server-stamped date (or cleared Closed Date)
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message: getErrorMessage(err, "Failed to update ticket status. Please try again.")
      });
      setTickets(prevTickets);
    }
  };

  const confirmResolution = async () => {
    const text = (resolutionPrompt?.text || "").trim();
    if (!text) return; // button is disabled, but guard anyway
    const { id, kind, value } = resolutionPrompt;
    setResolutionPrompt(null);
    if (kind === "pushed") await submitPushedChange(id, value, text);
    else await submitStatusChange(id, value, text);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${MASTER_API}/helpdesk/delete/${id}`);
      await fetchTickets();
      setAlert({ type: "success", message: "Ticket deleted successfully." });
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message: getErrorMessage(err, "Failed to delete the ticket. Please try again.")
      });
    }
  };

  // Called by the delete icon — just opens the confirm dialog, no delete yet
  const requestDelete = (ticket) => {
    setDeleteTarget(ticket);
  };

  // Called from the confirm dialog's "Delete" button
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    await handleDelete(id);
  };

  /* ===================== FILTER OPTIONS (react-select) ===================== */
  const mpc = configOptions.moduleProcessConfig; // raw rows

  // Full module list (unfiltered)
  const moduleOptions = useMemo(
    () => uniqueOptions(mpc.map((r) => r.module_name)),
    [mpc]
  );

  // Process options scoped to a chosen module. If no module chosen, show every
  // process across all modules (so the fallback filter row still works).
  const getProcessOptions = (moduleName) =>
    uniqueOptions(
      mpc
        .filter((r) => !moduleName || (r.module_name || "").trim() === moduleName)
        .map((r) => r.process_name)
    );

  // Sub Process options scoped to a chosen module + process. If neither is
  // chosen, show every sub process (lets the user pick sub process first and
  // have module/process auto-fill via handleChange above).
  const getSubProcessOptions = (moduleName, processName) =>
    uniqueOptions(
      mpc
        .filter(
          (r) =>
            (!moduleName || (r.module_name || "").trim() === moduleName) &&
            (!processName || (r.process_name || "").trim() === processName)
        )
        .map((r) => r.sub_process_name)
    );

  const developerOptions = useMemo(() => toOptions(configOptions.developer), [configOptions.developer]);
  const priorityOptions = useMemo(() => toOptions(configOptions.priority), [configOptions.priority]);
  const ticketStatusOptions = useMemo(() => toOptions(configOptions.ticketStatus), [configOptions.ticketStatus]);

  const ticketNoOptions = useMemo(
    () =>
      [...new Set(tickets.map((t) => t.ticket_no))]
        .sort((a, b) => a - b)
        .map((n) => ({ value: n, label: String(n) })),
    [tickets]
  );

  // "Created By" filter options — pulled straight from the helpdesk_tickets
  // table's registered_by column on the already-loaded tickets, deduped via
  // uniqueOptions (same helper used for module/process/sub-process above).
  const createdByOptions = useMemo(
    () => uniqueOptions(tickets.map((t) => t.registered_by)),
    [tickets]
  );

  const activeFilterCount = Object.entries(filters).filter(([key, v]) =>
    key === "status" || key === "createdBy" ? v && v.length > 0 : v !== null
  ).length;

  // "Select All" state for the Created By checkbox list
  const allCreatedBySelected =
    createdByOptions.length > 0 && filters.createdBy.length === createdByOptions.length;

  const handleCreatedByChange = (selected, meta) => {
    const clickedAll = meta?.option?.value === SELECT_ALL_VALUE;
    if (clickedAll) {
      // toggle: everything selected -> clear all, otherwise select all
      setFilters((prev) => ({
        ...prev,
        createdBy: allCreatedBySelected ? [] : createdByOptions
      }));
      return;
    }
    setFilters((prev) => ({
      ...prev,
      createdBy: (selected || []).filter((o) => o.value !== SELECT_ALL_VALUE)
    }));
  };

  const clearAllFilters = () =>
    setFilters({
      module: null,
      process: null,
      subProcess: null,
      developer: null,
      priority: null,
      status: [],
      ticketNo: null,
      createdBy: []
    });

  /* ===================== FILTERED TICKETS ===================== */
  // Applies every filter except the status tab — used to compute per-tab
  // counts that stay in sync with whatever the user has filtered on.
  const fieldFilteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      if (filters.module && t.module_name !== filters.module.value) return false;
      if (filters.process && t.process_name !== filters.process.value) return false;
      if (filters.subProcess && t.sub_process_name !== filters.subProcess.value) return false;
      if (filters.developer && t.developer !== filters.developer.value) return false;
      if (
        filters.priority &&
        String(t.priority || "").trim().toLowerCase() !==
          String(filters.priority.value || "").trim().toLowerCase()
      )
        return false;
      if (
        filters.status &&
        filters.status.length > 0 &&
        !filters.status.some((s) => s.value === t.developer_status)
      )
        return false;
      if (filters.ticketNo && String(t.ticket_no) !== String(filters.ticketNo.value)) return false;
      if (
        filters.createdBy.length > 0 &&
        !filters.createdBy.some((c) => c.value === t.registered_by)
      )
        return false;
      return true;
    });
  }, [tickets, filters]);

  const filteredTickets = useMemo(() => {
    return fieldFilteredTickets.filter((t) => getStatusTab(t.developer_status) === statusTab);
  }, [fieldFilteredTickets, statusTab]);

  const activeCount = useMemo(
    () => fieldFilteredTickets.filter((t) => getStatusTab(t.developer_status) === "active").length,
    [fieldFilteredTickets]
  );
  const deployedCount = useMemo(
    () => fieldFilteredTickets.filter((t) => getStatusTab(t.developer_status) === "deployed").length,
    [fieldFilteredTickets]
  );
  const customerActionCount = useMemo(
    () => fieldFilteredTickets.filter((t) => getStatusTab(t.developer_status) === "customerAction").length,
    [fieldFilteredTickets]
  );
  const closedCount = useMemo(
    () => fieldFilteredTickets.filter((t) => getStatusTab(t.developer_status) === "closed").length,
    [fieldFilteredTickets]
  );
  const holdCount = useMemo(
    () => fieldFilteredTickets.filter((t) => getStatusTab(t.developer_status) === "hold").length,
    [fieldFilteredTickets]
  );

  return (
    <Layout defaultSidebarOpen={false}>
      <style>{`
        .hd-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
        .hd-tab-toggle button { border: none; background: transparent; border-radius: 6px; padding: 5px 14px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.15s ease; display: flex; align-items: center; gap: 6px; white-space: nowrap; }
        .hd-tab-toggle button.hd-active-tab { background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
        .hd-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; border-radius: 9px; font-size: 11px; font-weight: 700; padding: 0 5px; line-height: 1; }
        .hd-badge-active { background: #dbeafe; color: #1d4ed8; }
        .hd-badge-deployed { background: #ede9fe; color: #6d28d9; }
        .hd-badge-customerAction { background: #fef3c7; color: #b45309; }
        .hd-badge-closed { background: #dcfce7; color: #15803d; }
        .hd-badge-hold { background: #fee2e2; color: #b91c1c; }
      `}</style>

      <div style={styles.page}>
        {alert && !showModal && (
          <div style={styles.alertBox(alert.type)}>
            <span>{alert.message}</span>
            <button
              onClick={() => setAlert(null)}
              style={{
                background: "none",
                border: "none",
                fontWeight: 700,
                cursor: "pointer",
                color: "inherit",
                fontSize: "14px",
                lineHeight: 1
              }}
            >
              &times;
            </button>
          </div>
        )}

        <div style={styles.pageTitleBar}>
          <div>
            <h2 style={styles.pageTitle}>Help Desk</h2>
            <p style={styles.pageSubtitle}>Manage and maintain support tickets</p>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn btn-outline-secondary"
            style={{ fontSize: 13, fontWeight: 600 }}
            disabled={fieldFilteredTickets.length === 0}
            onClick={() =>
              exportTicketsToCsv(
                fieldFilteredTickets,
                `helpdesk_tickets_${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
          >
            Export ({fieldFilteredTickets.length})
          </button>
          <button
            style={styles.createBtn}
            onClick={() => {
              setEditId(null);
              setTicketRows([createEmptyTicket(null)]);
              setAlert(null);
              setShowModal(true);
            }}
          >
            + Create Ticket
          </button>
          </div>
        </div>

        {/* ===================== FILTERS ===================== */}
        <div style={styles.filterCard}>
          {activeFilterCount > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                style={{ fontSize: 12, height: 28, whiteSpace: "nowrap" }}
                onClick={clearAllFilters}
              >
                &times; Clear All ({activeFilterCount})
              </button>
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <div style={{ minWidth: 130, flex: "1 1 130px" }}>
              <label style={styles.compactLabel}>Module</label>
              <Select
                styles={customSelectStyles}
                options={moduleOptions}
                value={filters.module}
                placeholder="Module"
                isClearable
                onChange={(v) =>
                  setFilters((prev) => ({
                    ...prev,
                    module: v,
                    process: null,
                    subProcess: null
                  }))
                }
              />
            </div>

            <div style={{ minWidth: 130, flex: "1 1 130px" }}>
              <label style={styles.compactLabel}>Process</label>
              <Select
                styles={customSelectStyles}
                options={getProcessOptions(filters.module?.value)}
                value={filters.process}
                placeholder="Process"
                isClearable
                onChange={(v) =>
                  setFilters((prev) => ({ ...prev, process: v, subProcess: null }))
                }
              />
            </div>

            <div style={{ minWidth: 130, flex: "1 1 130px" }}>
              <label style={styles.compactLabel}>Sub Process</label>
              <Select
                styles={customSelectStyles}
                options={getSubProcessOptions(filters.module?.value, filters.process?.value)}
                value={filters.subProcess}
                placeholder="Sub Process"
                isClearable
                onChange={(v) => {
                  // Direct sub-process pick auto-fills module + process filters too
                  const match = v
                    ? mpc.find((r) => (r.sub_process_name || "").trim() === v.value)
                    : null;
                  setFilters((prev) => ({
                    ...prev,
                    subProcess: v,
                    module: match
                      ? { value: (match.module_name || "").trim(), label: (match.module_name || "").trim() }
                      : prev.module,
                    process: match
                      ? { value: (match.process_name || "").trim(), label: (match.process_name || "").trim() }
                      : prev.process
                  }));
                }}
              />
            </div>

            <div style={{ minWidth: 130, flex: "1 1 130px" }}>
              <label style={styles.compactLabel}>Developer</label>
              <Select
                styles={customSelectStyles}
                options={developerOptions}
                value={filters.developer}
                placeholder="Developer"
                isClearable
                onChange={(v) => setFilters((prev) => ({ ...prev, developer: v }))}
              />
            </div>

            <div style={{ minWidth: 110, flex: "1 1 110px" }}>
              <label style={styles.compactLabel}>Priority</label>
              <Select
                styles={customSelectStyles}
                options={priorityOptions}
                value={filters.priority}
                placeholder="Priority"
                isClearable
                onChange={(v) => setFilters((prev) => ({ ...prev, priority: v }))}
              />
            </div>

            <div style={{ minWidth: 200, flex: "1 1 200px" }}>
              <label style={styles.compactLabel}>Status</label>
              <Select
                styles={multiSelectStyles}
                options={ticketStatusOptions}
                value={filters.status}
                placeholder="Status"
                isClearable
                isMulti
                onChange={(v) => setFilters((prev) => ({ ...prev, status: v || [] }))}
              />
            </div>

            <div style={{ minWidth: 130, flex: "1 1 130px" }}>
              <label style={styles.compactLabel}>Created By</label>
              <Select
                styles={checkboxSelectStyles}
                options={[{ value: SELECT_ALL_VALUE, label: "Select All" }, ...createdByOptions]}
                value={filters.createdBy}
                placeholder="Created By"
                isMulti
                isClearable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                allSelected={allCreatedBySelected}
                components={{
                  Option: CheckboxOption,
                  ValueContainer: CheckboxValueContainer,
                  MultiValue: NoMultiValue
                }}
                onChange={handleCreatedByChange}
              />
            </div>

            <div style={{ minWidth: 110, flex: "1 1 110px" }}>
              <label style={styles.compactLabel}>Ticket No</label>
              <Select
                styles={customSelectStyles}
                options={ticketNoOptions}
                value={filters.ticketNo}
                placeholder="Ticket No"
                isClearable
                onChange={(v) => setFilters((prev) => ({ ...prev, ticketNo: v }))}
              />
            </div>
          </div>
        </div>

        {/* ===================== STATUS TABS ===================== */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <div className="hd-tab-toggle">
            <button
              className={statusTab === "active" ? "hd-active-tab" : ""}
              onClick={() => setStatusTab("active")}
            >
              Active {activeCount > 0 && <span className="hd-badge hd-badge-active">{activeCount}</span>}
            </button>
            <button
              className={statusTab === "deployed" ? "hd-active-tab" : ""}
              onClick={() => setStatusTab("deployed")}
            >
              Deployed {deployedCount > 0 && <span className="hd-badge hd-badge-deployed">{deployedCount}</span>}
            </button>
            <button
              className={statusTab === "customerAction" ? "hd-active-tab" : ""}
              onClick={() => setStatusTab("customerAction")}
            >
              Customer Action {customerActionCount > 0 && (
                <span className="hd-badge hd-badge-customerAction">{customerActionCount}</span>
              )}
            </button>
            <button
              className={statusTab === "closed" ? "hd-active-tab" : ""}
              onClick={() => setStatusTab("closed")}
            >
              Closed {closedCount > 0 && <span className="hd-badge hd-badge-closed">{closedCount}</span>}
            </button>
            <button
              className={statusTab === "hold" ? "hd-active-tab" : ""}
              onClick={() => setStatusTab("hold")}
            >
              Hold {holdCount > 0 && <span className="hd-badge hd-badge-hold">{holdCount}</span>}
            </button>
          </div>
        </div>

        {/* ===================== TABLE ===================== */}
        <div style={styles.tableContainer}>
          <table className="table" style={styles.erpTable}>
            <thead>
              <tr>
                <th style={styles.erpTh}>Ticket No</th>
                <th style={styles.erpTh}>Module</th>
                <th style={styles.erpTh}>Process</th>
                <th style={styles.erpTh}>Sub Process</th>
                <th style={styles.erpTh}>Description</th>
                <th style={styles.erpTh}>Created By</th>
                <th style={styles.erpTh}>Creation Date</th>
                <th style={styles.erpTh}>Ageing (Days)</th>
                <th style={styles.erpTh}>Developer</th>
                <th style={styles.erpTh}>Priority</th>
                <th style={styles.erpTh}>Status</th>
                <th style={styles.erpTh}></th>
                {statusTab === "active" && <th style={styles.erpTh}>Pushed</th>}
              </tr>
            </thead>

            <tbody>
              {filteredTickets.length > 0 ? (
                filteredTickets.map((ticket, index) => (
                  <tr key={index}>
                    <td style={styles.erpTd}>{ticket.ticket_no}</td>
                    <td style={styles.erpTd}>{ticket.module_name || "-"}</td>
                    <td style={styles.erpTd}>{ticket.process_name || "-"}</td>
                    <td style={styles.erpTd}>{ticket.sub_process_name || "-"}</td>
                    <td style={styles.erpTd}>{ticket.ticket_description || "-"}</td>
                    <td style={styles.erpTd}>{ticket.registered_by || "-"}</td>
                    <td style={styles.erpTd}>
                      {ticket.creation_date
                        ? formatDMY(String(ticket.creation_date).split("T")[0]) || "-"
                        : "-"}
                    </td>
                    <td style={styles.erpTd}>{getTicketAgeing(ticket)}</td>
                    <td style={styles.erpTd}>
                      {statusTab === "active" ? (
                        <select
                          className="form-select form-select-sm"
                          style={{ fontSize: "13px", width: "150px" }}
                          value={ticket.developer || ""}
                          onChange={(e) => handleDeveloperChange(ticket.id, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {developerOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        ticket.developer || "-"
                      )}
                    </td>
                    <td style={styles.erpTd}>{ticket.priority || "-"}</td>
                    <td style={styles.erpTd}>
                      {statusTab === "active" ? (
                        ticket.developer_status || "-"
                      ) : (
                        <select
                          className="form-select form-select-sm"
                          style={{ fontSize: "13px", width: "150px" }}
                          value={ticket.developer_status || ""}
                          onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                        >
                          {!ticket.developer_status && <option value="">Select</option>}
                          {ticketStatusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td style={styles.erpTd}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <button
                          className="btn btn-outline-dark btn-sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                          onClick={() => handleEdit(ticket)}
                        >
                          Edit
                        </button>
                        <button
                          title="Delete"
                          style={{
                            background: "#fff",
                            border: "1px solid #f5c2c7",
                            borderRadius: "4px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "4px 8px",
                            cursor: "pointer"
                          }}
                          onClick={() => requestDelete(ticket)}
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    {statusTab === "active" && (
                      <td style={styles.erpTd}>
                        <select
                          className="form-select form-select-sm"
                          style={{
                            fontSize: "13px",
                            width: "90px",
                            fontWeight: 700,
                            color: ticket.pushed_status === "yes" ? "#166534" : "#b91c1c"
                          }}
                          value={ticket.pushed_status || "no"}
                          onChange={(e) => handlePushedChange(ticket.id, e.target.value)}
                        >
                          <option value="yes">✔</option>
                          <option value="no">✘</option>
                        </select>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={statusTab === "active" ? 13 : 12} className="text-center text-muted p-3">
                    No tickets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ===================== CREATE / EDIT MODAL ===================== */}
        {showModal && (
          <div style={styles.modalBackdrop}>
            <div style={styles.modal}>
              <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 style={{ margin: 0 }}>
                    {editId ? "Edit" : "Create"}
                  </h4>

                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      setShowModal(false);
                      setAlert(null);
                    }}
                  >
                    X
                  </button>
                </div>

                {alert && (
                  <div style={styles.alertBox(alert.type)}>
                    <span>{alert.message}</span>
                    <button
                      onClick={() => setAlert(null)}
                      style={{
                        background: "none",
                        border: "none",
                        fontWeight: 700,
                        cursor: "pointer",
                        color: "inherit",
                        fontSize: "14px",
                        lineHeight: 1
                      }}
                    >
                      &times;
                    </button>
                  </div>
                )}

                {ticketRows.map((row, index) => (
                  <div key={index} className="border rounded p-4 mb-4">
                    <div className="d-flex justify-content-between mb-3">
                      <h5>{row.ticketNo ? `Ticket #${row.ticketNo}` : ""}</h5>

                      {ticketRows.length > 1 && (
                        <button className="btn btn-sm btn-danger" onClick={() => removeRow(index)}>
                          Remove
                        </button>
                      )}
                    </div>

                    <div
                      className="mb-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: "12px"
                      }}
                    >
                      <div>
                        <label style={styles.compactLabel}>
                          Module <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <Select
                          styles={{
                            ...customSelectStyles,
                            control: (base, state) => ({
                              ...customSelectStyles.control(base, state),
                              borderColor:
                                alert && !row.moduleName ? "#dc3545" : customSelectStyles.control(base, state).borderColor
                            })
                          }}
                          options={moduleOptions}
                          value={
                            row.moduleName
                              ? { value: row.moduleName, label: row.moduleName }
                              : null
                          }
                          placeholder="Select"
                          isClearable
                          onChange={(v) => handleChange(index, "moduleName", v ? v.value : "")}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Process</label>
                        <Select
                          styles={customSelectStyles}
                          options={getProcessOptions(row.moduleName)}
                          value={row.process ? { value: row.process, label: row.process } : null}
                          placeholder="Select"
                          isClearable
                          onChange={(v) => handleChange(index, "process", v ? v.value : "")}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Sub Process</label>
                        <Select
                          styles={customSelectStyles}
                          options={getSubProcessOptions(row.moduleName, row.process)}
                          value={
                            row.subProcess
                              ? { value: row.subProcess, label: row.subProcess }
                              : null
                          }
                          placeholder="Select"
                          isClearable
                          onChange={(v) => handleChange(index, "subProcess", v ? v.value : "")}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Created By</label>
                        <input
                          className="form-control"
                          style={{ ...styles.compactInput, background: "#f8fafc" }}
                          value={row.registeredBy}
                          readOnly
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Created Date</label>
                        <DateField value={row.creationDate} readOnly />
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Developer</label>
                        <Select
                          styles={customSelectStyles}
                          options={developerOptions}
                          value={
                            row.developer ? { value: row.developer, label: row.developer } : null
                          }
                          placeholder="Select"
                          isClearable
                          onChange={(v) => handleChange(index, "developer", v ? v.value : "")}
                        />
                      </div>

                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Priority</label>
                        <Select
                          styles={customSelectStyles}
                          options={priorityOptions}
                          value={
                            row.priority !== ""
                              ? { value: row.priority, label: String(row.priority) }
                              : null
                          }
                          placeholder="Select"
                          isClearable
                          onChange={(v) => handleChange(index, "priority", v ? v.value : "")}
                        />
                      </div>

                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Status</label>
                        <Select
                          styles={customSelectStyles}
                          options={ticketStatusOptions}
                          value={
                            row.developerStatus
                              ? { value: row.developerStatus, label: row.developerStatus }
                              : null
                          }
                          placeholder="Select"
                          isClearable
                          onChange={(v) => handleChange(index, "developerStatus", v ? v.value : "")}
                        />
                      </div>
                    </div>

                    <div
                      className="mb-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: "12px",
                        marginTop: "20px"
                      }}
                    >
                      <div>
                        <label style={styles.compactLabel}>Estimated Efforts</label>
                        <input
                          className="form-control"
                          style={styles.compactInput}
                          placeholder="e.g. 4h"
                          value={row.estimatedEfforts}
                          onChange={(e) => handleChange(index, "estimatedEfforts", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Estimated Start Date</label>
                        <DateField
                          value={row.estimatedStartDate}
                          onChange={(e) => handleChange(index, "estimatedStartDate", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Estimated Start Time</label>
                        <input
                          type="time"
                          className="form-control"
                          style={styles.compactInput}
                          value={row.estimatedStartTime}
                          onChange={(e) => handleChange(index, "estimatedStartTime", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Estimated Closure Date</label>
                        <DateField
                          value={row.estimatedClosureDate}
                          onChange={(e) => handleChange(index, "estimatedClosureDate", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Estimated Closure Time</label>
                        <input
                          type="time"
                          className="form-control"
                          style={styles.compactInput}
                          value={row.estimatedClosureTime}
                          onChange={(e) => handleChange(index, "estimatedClosureTime", e.target.value)}
                        />
                      </div>
                    </div>

                    <div
                      className="mb-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: "12px"
                      }}
                    >
                      <div>
                        <label style={styles.compactLabel}>Actual Efforts</label>
                        <input
                          className="form-control"
                          style={styles.compactInput}
                          placeholder="e.g. 4h"
                          value={row.actualEfforts}
                          onChange={(e) => handleChange(index, "actualEfforts", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Actual Start Date</label>
                        <DateField
                          value={row.actualStartDate}
                          onChange={(e) => handleChange(index, "actualStartDate", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Actual Start Time</label>
                        <input
                          type="time"
                          className="form-control"
                          style={styles.compactInput}
                          value={row.actualStartTime}
                          onChange={(e) => handleChange(index, "actualStartTime", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Actual Closure Date</label>
                        <DateField
                          value={row.actualClosureDate}
                          onChange={(e) => handleChange(index, "actualClosureDate", e.target.value)}
                        />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Actual Closure Time</label>
                        <input
                          type="time"
                          className="form-control"
                          style={styles.compactInput}
                          value={row.actualClosureTime}
                          onChange={(e) => handleChange(index, "actualClosureTime", e.target.value)}
                        />
                      </div>
                    </div>

                    <div
                      className="mb-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: "12px"
                      }}
                    >
                      <div>
                        <label style={styles.compactLabel}>Development Start Date</label>
                        <DateField value={row.developmentStartDate} readOnly />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Development End Date</label>
                        <DateField value={row.developmentEndDate} readOnly />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Deployment Date</label>
                        <DateField value={row.deploymentDate} readOnly />
                      </div>

                      <div>
                        <label style={styles.compactLabel}>Closed Date</label>
                        <DateField value={row.closedDate} readOnly />
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-12">
                        <label style={styles.compactLabel}>Ticket Description</label>
                        <textarea
                          rows="4"
                          className="form-control"
                          value={row.ticketDescription}
                          onChange={(e) => handleChange(index, "ticketDescription", e.target.value)}
                        />
                      </div>

                      <div className="col-md-12">
                        <label style={styles.compactLabel}>
                          Resolution Provided
                          {editId &&
                            (tickets.find((t) => t.id === editId)?.developer_status || "").trim().toLowerCase() === "in development" &&
                            (row.developerStatus || "").trim().toLowerCase() !== "in development" && (
                              <span style={{ color: "#dc2626" }}> *</span>
                            )}
                        </label>
                        <textarea
                          rows="4"
                          className="form-control"
                          value={row.resolutionProvided}
                          onChange={(e) => handleChange(index, "resolutionProvided", e.target.value)}
                        />
                      </div>

                      <div className="col-md-12">
                        <label style={styles.compactLabel}>Commit Name</label>
                        {getCommitList(row.commitName).map((commit, ci, list) => (
                          <div
                            key={ci}
                            style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}
                          >
                            <input
                              className="form-control"
                              style={styles.compactInput}
                              placeholder={`Commit ${ci + 1}`}
                              value={commit}
                              onChange={(e) => {
                                const next = [...list];
                                next[ci] = e.target.value;
                                handleChange(index, "commitName", next.join("\n"));
                              }}
                            />
                            {list.length > 1 && (
                              <button
                                type="button"
                                title="Remove this commit"
                                className="btn btn-outline-danger btn-sm"
                                style={{ padding: "2px 10px", fontSize: 14, lineHeight: 1.2 }}
                                onClick={() =>
                                  handleChange(
                                    index,
                                    "commitName",
                                    list.filter((_, i) => i !== ci).join("\n")
                                  )
                                }
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          style={{ fontSize: 12, fontWeight: 600 }}
                          onClick={() =>
                            handleChange(
                              index,
                              "commitName",
                              [...getCommitList(row.commitName), ""].join("\n")
                            )
                          }
                        >
                          + Add Commit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="d-flex justify-content-end mt-3">
                  <button style={styles.createBtn} onClick={handleSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================== TOAST ===================== */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: 20,
              right: 20,
              zIndex: 99999,
              minWidth: 280,
              maxWidth: "90vw",
              padding: "12px 16px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 12,
              boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
              background: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
              color: toast.type === "error" ? "#b91c1c" : "#15803d",
              border: `1px solid ${toast.type === "error" ? "#fecaca" : "#bbf7d0"}`
            }}
          >
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700 }}
            >
              ×
            </button>
          </div>
        )}

        {/* ===================== RESOLUTION REQUIRED MODAL ===================== */}
        {resolutionPrompt && (
          <div style={styles.modalBackdrop} onClick={() => setResolutionPrompt(null)}>
            <div
              style={{ ...styles.modal, width: "460px", maxWidth: "90vw" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <h5 style={{ margin: "0 0 10px 0" }}>Resolution Provided</h5>
                <p style={{ fontSize: "13px", color: "#334155", margin: "0 0 10px 0" }}>
                  Enter the resolution before moving this ticket out of In Development.
                </p>
                <textarea
                  rows="4"
                  autoFocus
                  className="form-control"
                  value={resolutionPrompt.text}
                  onChange={(e) =>
                    setResolutionPrompt((p) => ({ ...p, text: e.target.value }))
                  }
                />
                <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setResolutionPrompt(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!resolutionPrompt.text.trim()}
                    onClick={confirmResolution}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================== DELETE CONFIRM MODAL ===================== */}
        {deleteTarget && (
          <div style={styles.modalBackdrop} onClick={() => setDeleteTarget(null)}>
            <div
              style={{ ...styles.modal, width: "380px", maxWidth: "90vw" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <h5 style={{ margin: "0 0 10px 0" }}>Delete Ticket</h5>
                <p style={{ fontSize: "13px", color: "#334155", margin: "0 0 20px 0" }}>
                  Are you sure you want to delete ticket
                  {deleteTarget.ticket_no ? ` #${deleteTarget.ticket_no}` : ""}? This
                  action cannot be undone.
                </p>
                <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setDeleteTarget(null)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={confirmDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HelpDesk;